import * as R from "ramda";

import { bool, func, number, object, string, array } from "prop-types";

import cx from "classnames";
import useMatchMedia from "../../common/useMatchMedia";

const propTypes = {
  clearAllFacets: func,
  activeFacets: object,
  facetMenuOpen: bool,
  totalResults: number,
  onFacetClick: func,
  setFacetMenuOpen: func,
  clearAllLabel: string,
  facets: array,
  view: string,
  setView: func,
};

export const DropdownFacetBubbles = (props) => {
  const {
    activeFacets,
    totalResults,
    onFacetClick,
    clearAllFacets,
    facetMenuOpen,
    setFacetMenuOpen,
    clearAllLabel,
    facets,
    view,
    setView,
  } = props;

  // We only want to display active facets; this gets tricky when
  // selecting a facet in one dropdown can clear a selected facet(s) in another dropdown.
  // So, we need to check for facets where value === selected in the response.
  let bubbleObj = {};

  // Looping through facets (these come from the response)
  facets?.forEach((facet) => {
    let selectedArr = [];
    facet.values.forEach((value) => {
      if (value.selected) {
        // If a "top level" value has been selected,
        // add it to our selected array
        selectedArr.push(value.name);
      }
    }),
      facet.childGroups?.forEach((group) => {
        group.values?.forEach((value) => {
          if (value.selected) {
            // If a "child level" value has been selected,
            // add it to our selected array
            selectedArr.push(value.name);
          }
        });
      });
    // Add the array of selected facets to the bubble object
    // We're using an object here to easily work with pre-existing code.
    // Examples of what facet.id is: departments, topics
    bubbleObj[facet.id] = selectedArr;
  });

  // Build each facet bubble using the bubble object
  const bubbles = R.compose(
    R.unnest,
    R.map((item) => {
      const [facetId, values] = item;

      return values.map((val) => (
        <button
          key={val}
          className="facet-bubbles__single-facet"
          aria-label="Remove facet"
          type="button"
          onClick={() => onFacetClick(facetId, val)}
        >
          {closeIcon}
          <span>{val}</span>
        </button>
      ));
    }),
    R.toPairs,
  )(bubbleObj);

  // Iterate through the bubble object to see if there are any selected facets
  // This determines whether or not to show the facet bubble UI elements.
  let selectedFilters = false;
  for (const property in bubbleObj) {
    if (bubbleObj[property].length > 0) {
      selectedFilters = true;
      break;
    }
  }

  const matchesLarge = useMatchMedia("(min-width: 1200px)");

  return (
    <div
      className={cx("facet-bubbles", {
        "facet-bubbles--facet-menu-open": facetMenuOpen,
        "facet-bubbles--display-mobile": !matchesLarge && bubbles.length > 0,
      })}
    >
      {bubbles.length > 0 && (
        <>
          <span className="facet-bubbles__total-results">
            ({totalResults} {totalResults === 1 ? "result" : "results"})
          </span>

          <button
            onClick={() => {
              clearAllFacets();
            }}
            className="facet-bubbles__remove"
            aria-label="Clear all facets"
            type="button"
          >
            {closeIcon}
            <span>{clearAllLabel}</span>
          </button>

          {bubbles}
        </>
      )}

      {matchesLarge && (
        <button
          type="button"
          className="facet-bubbles__view-toggle"
          onClick={() => setView(view === "grid" ? "list" : "grid")}
        >
          {view === "grid" ? (
            <>List view {listIcon}</>
          ) : (
            <>Grid view {gridIcon}</>
          )}
        </button>
      )}
    </div>
  );
};

DropdownFacetBubbles.propTypes = propTypes;
export default DropdownFacetBubbles;

const closeIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="feather feather-x"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const listIcon = (
  <svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
    <g id="Group_1626">
      <rect
        id="Rectangle_471"
        className="cls-1"
        x=".96"
        y="4.64"
        width="2.5"
        height="2.5"
      />
      <rect
        id="Rectangle_477"
        className="cls-1"
        x=".96"
        y="8.64"
        width="2.5"
        height="2.5"
      />
      <rect
        id="Rectangle_479"
        className="cls-1"
        x=".96"
        y="12.64"
        width="2.5"
        height="2.5"
      />
      <rect
        id="Rectangle_476"
        className="cls-1"
        x="4.96"
        y="4.64"
        width="14"
        height="2.5"
      />
      <rect
        id="Rectangle_478"
        className="cls-1"
        x="4.96"
        y="8.64"
        width="14"
        height="2.5"
      />
      <rect
        id="Rectangle_480"
        className="cls-1"
        x="4.96"
        y="12.64"
        width="14"
        height="2.5"
      />
    </g>
  </svg>
);

const gridIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
    <g id="Group_1640">
      <rect
        id="Rectangle_471"
        className="cls-1"
        x="4.48"
        y="4.57"
        width="2.5"
        height="2.5"
      />
      <rect
        id="Rectangle_477"
        className="cls-1"
        x="4.48"
        y="8.57"
        width="2.5"
        height="2.5"
      />
      <rect
        id="Rectangle_479"
        className="cls-1"
        x="4.48"
        y="12.57"
        width="2.5"
        height="2.5"
      />
    </g>
    <g id="Group_1641">
      <rect
        id="Rectangle_471-2"
        className="cls-1"
        x="8.48"
        y="4.57"
        width="2.5"
        height="2.5"
      />
      <rect
        id="Rectangle_477-2"
        className="cls-1"
        x="8.48"
        y="8.57"
        width="2.5"
        height="2.5"
      />
      <rect
        id="Rectangle_479-2"
        className="cls-1"
        x="8.48"
        y="12.57"
        width="2.5"
        height="2.5"
      />
    </g>
    <g id="Group_1642">
      <rect
        id="Rectangle_471-3"
        className="cls-1"
        x="12.23"
        y="4.57"
        width="2.5"
        height="2.5"
      />
      <rect
        id="Rectangle_477-3"
        className="cls-1"
        x="12.23"
        y="8.57"
        width="2.5"
        height="2.5"
      />
      <rect
        id="Rectangle_479-3"
        className="cls-1"
        x="12.23"
        y="12.57"
        width="2.5"
        height="2.5"
      />
    </g>
  </svg>
);
